// ============================================================================
// RBAC.JS — Role-Based Access Control (v2)
//
// 10-role catalog matching the full referral ecosystem.
// Sector-based routing: every app route belongs to a SECTOR; roles map to
// allowed SECTORS. canAccessSector() is the single check for both RouteGuard
// and sidebar visibility.
// ============================================================================

// ============================================================================
// ROLE ENUM
// ============================================================================

export const ROLES = {
  // Platform layer
  PLATFORM_ADMIN: 'platform_admin',

  // Tenant (clinic) admin + ops
  TENANT_ADMIN: 'tenant_admin',
  OPS_MANAGER: 'ops_manager',

  // Referring side (GP clinic)
  GP_EMPLOYEE: 'gp_employee',

  // Intake / triage / scheduling
  INTAKE_COORDINATOR: 'intake_coordinator',
  TRIAGE_NURSE: 'triage_nurse',
  SCHEDULER: 'scheduler',

  // Receiving side (specialty clinic)
  SPECIALIST:       'specialist',
  SPECIALIST_ADMIN: 'specialist_admin',

  // Bootstrap / safety
  PENDING: 'pending',
};

// Canonical ordered list — must exactly match Firestore rules enum
export const ROLE_ENUM = [
  'pending',
  'gp_employee',
  'intake_coordinator',
  'triage_nurse',
  'scheduler',
  'specialist',
  'specialist_admin',
  'ops_manager',
  'tenant_admin',
  'platform_admin',
];

// ============================================================================
// SECTORS — named app modules gated by role
// ============================================================================

export const SECTORS = {
  PENDING:        'SECTOR_PENDING',         // Access Pending / provisioning required
  GATE:           'SECTOR_GATE',            // GateView: run gate + Submit
  NOT_YET:        'SECTOR_NOT_YET',         // Not-Yet Inbox
  WORKQUEUE:      'SECTOR_WORKQUEUE',       // All Referrals queue
  TRIAGE:         'SECTOR_TRIAGE',          // Triage tools (nurse workflow)
  SCHEDULING:           'SECTOR_SCHEDULING',           // Scheduling handshake states
  SPECIALIST_WORKLIST:  'SECTOR_SPECIALIST_WORKLIST',  // Specialist post-consult worklist
  CLOSED_LOOP:          'SECTOR_CLOSED_LOOP',           // Loop closed / consult note returned
  PACKS:          'SECTOR_PACKS',           // Pack Manager
  ANALYTICS:      'SECTOR_ANALYTICS',       // Dashboards; reads rollups/metrics
  AUDIT:          'SECTOR_AUDIT',           // Audit Logs
  OPS:            'SECTOR_OPS',             // Ops error inbox
  TENANT_ADMIN:   'SECTOR_TENANT_ADMIN',    // User mgmt + tenant settings
  PLATFORM_ADMIN: 'SECTOR_PLATFORM_ADMIN',  // Tenants mgmt + cross-tenant rollups
  PACKET_INBOX:   'SECTOR_PACKET_INBOX',    // Phase 1: Referral Packet Inbox Foundation
};

// ============================================================================
// SECTOR PERMISSIONS — role → allowed sectors[]
// This is the canonical RBAC allowlist. All route guards derive from here.
// ============================================================================

export const SECTOR_PERMISSIONS = {
  [ROLES.PLATFORM_ADMIN]: Object.values(SECTORS), // all sectors

  [ROLES.TENANT_ADMIN]: [
    SECTORS.TENANT_ADMIN,
    SECTORS.ANALYTICS,
    SECTORS.AUDIT,
    SECTORS.OPS,
    SECTORS.WORKQUEUE,  // read
    SECTORS.PACKET_INBOX, // Phase 1: operational visibility (read-only)
  ],

  [ROLES.OPS_MANAGER]: [
    SECTORS.ANALYTICS,      // ops-dashboard only
    SECTORS.TENANT_ADMIN,   // impact-settings (read-only)
    SECTORS.PACKET_INBOX,   // Phase 1: operational visibility (read-only)
  ],

  [ROLES.GP_EMPLOYEE]: [
    SECTORS.GATE,
    SECTORS.NOT_YET,    // own referrals only
    SECTORS.PACKET_INBOX, // Phase 1: upload + Start New Referral from packet
  ],

  [ROLES.INTAKE_COORDINATOR]: [
    SECTORS.GATE,       // complete NOT_YET referrals on behalf of referring provider
    SECTORS.WORKQUEUE,
    SECTORS.NOT_YET,    // tenant-wide view
    SECTORS.ANALYTICS,  // Ops Home (Stage 7)
    SECTORS.PACKET_INBOX, // Phase 1: upload + view inbox (no Start New Referral)
    // SECTOR_TRIAGE / rework added here later when built out
  ],

  [ROLES.TRIAGE_NURSE]: [
    SECTORS.TRIAGE,     // Triage Queue + My Triage History
  ],

  [ROLES.SCHEDULER]: [
    SECTORS.WORKQUEUE,  // read
    SECTORS.SCHEDULING,
    SECTORS.ANALYTICS,  // Ops Home (Stage 7)
  ],

  [ROLES.SPECIALIST]: [
    SECTORS.SPECIALIST_WORKLIST,  // post-consult worklist (complete consults)
    SECTORS.PACKS,                // author criteria for their specialties
    SECTORS.ANALYTICS,            // specialty-scoped
    SECTORS.AUDIT,                // read, specialty-scoped
    // SECTORS.GATE is intentionally omitted — specialist is receiving-side in v2.
    // Add SECTORS.GATE here if you want specialist to run gate.
  ],

  [ROLES.SPECIALIST_ADMIN]: [
    SECTORS.SPECIALIST_WORKLIST,  // closure-focused workplace (SEEN → CLOSED_LOOP)
  ],

  [ROLES.PENDING]: [
    SECTORS.PENDING,      // Access Pending screen only
  ],
};

// ============================================================================
// DEFAULT LANDING ROUTES — deterministic per role
// ============================================================================

export const DEFAULT_LANDING = {
  [ROLES.PLATFORM_ADMIN]:     'dashboard',
  [ROLES.TENANT_ADMIN]:       'ops-dashboard',
  [ROLES.OPS_MANAGER]:        'ops-dashboard',
  [ROLES.GP_EMPLOYEE]:        'packet-inbox',
  [ROLES.INTAKE_COORDINATOR]: 'workqueue',
  [ROLES.TRIAGE_NURSE]:       'triage-queue',
  [ROLES.SCHEDULER]:          'scheduling',
  [ROLES.SPECIALIST]:         'specialist-worklist',
  [ROLES.SPECIALIST_ADMIN]:   'specialist-admin-workplace',
  [ROLES.PENDING]:            'pending',
};

/** Returns the default landing route for a role. Preferred over DEFAULT_LANDING map. */
export const defaultRouteForRole = (role) =>
  DEFAULT_LANDING[role] ?? 'dashboard';

// ============================================================================
// PERMISSIONS (legacy — kept for backward compat)
// New code should use canAccessSector(role, sector).
// ============================================================================

export const PERMISSIONS = {
  GATE_RUN: 'gate:run',
  METRICS_CREATE: 'metrics:create',
  ANALYTICS_OWN: 'analytics:own',
  ANALYTICS_SPECIALTY: 'analytics:specialty',
  ANALYTICS_TENANT: 'analytics:tenant',
  ANALYTICS_PLATFORM: 'analytics:platform',
  ANALYTICS_EXPORT: 'analytics:export',
  PACK_VIEW: 'pack:view',
  PACK_MANAGE: 'pack:manage',
  USER_VIEW_TENANT: 'user:view:tenant',
  USER_MANAGE_TENANT: 'user:manage:tenant',
  USER_MANAGE_ALL: 'user:manage:all',
  TENANT_VIEW: 'tenant:view',
  TENANT_MANAGE: 'tenant:manage',
  PLATFORM_STATS: 'platform:stats',
};

export const ROLE_PERMISSIONS = {
  [ROLES.GP_EMPLOYEE]: [
    PERMISSIONS.GATE_RUN,
    PERMISSIONS.METRICS_CREATE,
    PERMISSIONS.ANALYTICS_OWN,
    PERMISSIONS.PACK_VIEW,
  ],
  [ROLES.INTAKE_COORDINATOR]: [PERMISSIONS.PACK_VIEW],
  [ROLES.TRIAGE_NURSE]: [PERMISSIONS.PACK_VIEW],
  [ROLES.SCHEDULER]: [],
  [ROLES.SPECIALIST]: [
    PERMISSIONS.ANALYTICS_SPECIALTY,
    PERMISSIONS.ANALYTICS_EXPORT,
    PERMISSIONS.PACK_VIEW,
    PERMISSIONS.PACK_MANAGE,
  ],
  [ROLES.SPECIALIST_ADMIN]: [],
  [ROLES.OPS_MANAGER]: [
    PERMISSIONS.ANALYTICS_TENANT,
    PERMISSIONS.ANALYTICS_EXPORT,
  ],
  [ROLES.TENANT_ADMIN]: [
    PERMISSIONS.ANALYTICS_TENANT,
    PERMISSIONS.ANALYTICS_EXPORT,
    PERMISSIONS.USER_VIEW_TENANT,
    PERMISSIONS.USER_MANAGE_TENANT,
    PERMISSIONS.TENANT_VIEW,
    PERMISSIONS.PACK_VIEW,
  ],
  [ROLES.PLATFORM_ADMIN]: [
    PERMISSIONS.PLATFORM_STATS,
    PERMISSIONS.TENANT_VIEW,
    PERMISSIONS.TENANT_MANAGE,
    PERMISSIONS.USER_VIEW_TENANT,
    PERMISSIONS.USER_MANAGE_ALL,
    PERMISSIONS.PACK_VIEW,
    PERMISSIONS.PACK_MANAGE,
  ],
  [ROLES.PENDING]: [],
};

// ============================================================================
// ROLE DISPLAY
// ============================================================================

export const ROLE_DISPLAY = {
  [ROLES.PLATFORM_ADMIN]:     { name: 'Platform Admin',      color: 'amber',   description: 'Manage platform, tenants, packs' },
  [ROLES.TENANT_ADMIN]:       { name: 'Tenant Admin',         color: 'violet',  description: 'Tenant analytics, ops, manage staff' },
  [ROLES.OPS_MANAGER]:        { name: 'Ops Manager',          color: 'cyan',    description: 'Analytics and KPI dashboards (read-only)' },
  [ROLES.GP_EMPLOYEE]:        { name: 'Referring Provider',   color: 'emerald', description: 'Run referrals, view own submissions' },
  [ROLES.INTAKE_COORDINATOR]: { name: 'Intake Coordinator',   color: 'teal',    description: 'Manage referral workqueue' },
  [ROLES.TRIAGE_NURSE]:       { name: 'Triage Nurse',         color: 'orange',  description: 'Clinical intake triage decisions' },
  [ROLES.SCHEDULER]:          { name: 'Scheduler',            color: 'indigo',  description: 'Scheduling handshake and status' },
  [ROLES.SPECIALIST]:         { name: 'Specialist',           color: 'blue',    description: 'Author packs, specialty analytics' },
  [ROLES.SPECIALIST_ADMIN]:   { name: 'Specialist Admin',     color: 'sky',     description: 'Seal closed loop, author packs' },
  [ROLES.PENDING]:            { name: 'Pending',              color: 'slate',   description: 'Account not yet provisioned' },
};

// ============================================================================
// SECTOR HELPERS
// ============================================================================

/** Returns true if role is allowed to access the given sector. */
export const canAccessSector = (role, sector) =>
  (SECTOR_PERMISSIONS[role] ?? []).includes(sector);

/** Returns true if role is in the canonical enum. */
export const isValidRole = (role) => ROLE_ENUM.includes(role);

// ============================================================================
// PERMISSION HELPERS (legacy)
// ============================================================================

export const hasPermission = (role, permission) =>
  (ROLE_PERMISSIONS[role] ?? []).includes(permission);

export const canRunGate = (role) => hasPermission(role, PERMISSIONS.GATE_RUN);
export const canCreateMetrics = (role) => hasPermission(role, PERMISSIONS.METRICS_CREATE);
export const canManagePacks = (role) => hasPermission(role, PERMISSIONS.PACK_MANAGE);
export const canManageTenants = (role) => hasPermission(role, PERMISSIONS.TENANT_MANAGE);
export const canManageAllUsers = (role) => hasPermission(role, PERMISSIONS.USER_MANAGE_ALL);
export const canManageTenantUsers = (role) =>
  hasPermission(role, PERMISSIONS.USER_MANAGE_TENANT) ||
  hasPermission(role, PERMISSIONS.USER_MANAGE_ALL);
export const canViewPlatformStats = (role) => hasPermission(role, PERMISSIONS.PLATFORM_STATS);
export const canViewTenantAnalytics = (role) => hasPermission(role, PERMISSIONS.ANALYTICS_TENANT);
export const canViewSpecialtyAnalytics = (role) => hasPermission(role, PERMISSIONS.ANALYTICS_SPECIALTY);
export const canViewOwnAnalytics = (role) => hasPermission(role, PERMISSIONS.ANALYTICS_OWN);
export const canExportAnalytics = (role) => hasPermission(role, PERMISSIONS.ANALYTICS_EXPORT);

// ============================================================================
// ROLE CHECKERS
// ============================================================================

export const isPlatformAdmin    = (role) => role === ROLES.PLATFORM_ADMIN;
export const isTenantAdmin      = (role) => role === ROLES.TENANT_ADMIN;
export const isOpsManager       = (role) => role === ROLES.OPS_MANAGER;
export const isGpEmployee       = (role) => role === ROLES.GP_EMPLOYEE;
export const isIntakeCoordinator = (role) => role === ROLES.INTAKE_COORDINATOR;
export const isTriageNurse      = (role) => role === ROLES.TRIAGE_NURSE;
export const isScheduler        = (role) => role === ROLES.SCHEDULER;
export const isSpecialist       = (role) => role === ROLES.SPECIALIST;
export const isSpecialistAdmin  = (role) => role === ROLES.SPECIALIST_ADMIN;
export const isSpecialistSide   = (role) => role === ROLES.SPECIALIST || role === ROLES.SPECIALIST_ADMIN;
export const isPending          = (role) => role === ROLES.PENDING;

/** Clinician: can submit referrals via the Gate.
 *  gp_employee is the primary author; intake_coordinator may complete NOT_YET
 *  referrals on behalf of the referring provider.
 *  Specialist is on the RECEIVING side and does not submit referrals. */
export const isClinician = (role) => isGpEmployee(role) || isIntakeCoordinator(role);

/**
 * Canonical roles allowed to upload documents into a pre-referral packet.
 * MUST stay in sync with the server enforcement list
 * `PACKET_UPLOAD_ROLES` in functions/packets.js (currently
 * ['gp_employee', 'intake_coordinator']). This is the single source of truth
 * for the client-side packet-upload permission so the UI cannot drift from the
 * backend — every packet-upload affordance (new-packet upload, EHR "Add
 * supporting documents") derives visibility from canUploadPacketDocuments().
 */
export const PACKET_UPLOAD_ROLES = Object.freeze(['gp_employee', 'intake_coordinator']);

/** True when the role may upload documents into a packet (client mirror of the
 *  server PACKET_UPLOAD_ROLES gate). gp_employee is the primary uploader. */
export const canUploadPacketDocuments = (role) => PACKET_UPLOAD_ROLES.includes(role);

// ============================================================================
// ROUTE GUARDS
// Routes → sectors (for RouteGuard sector check)
// ============================================================================

/** Maps a route id to the sector that guards it. */
export const ROUTE_TO_SECTOR = {
  // Platform admin
  tenants:           SECTORS.PLATFORM_ADMIN,
  'phi-policy':      SECTORS.PLATFORM_ADMIN,

  // Tenant admin + platform admin
  users:             SECTORS.TENANT_ADMIN,
  'impact-settings': SECTORS.TENANT_ADMIN,
  'ehr-integration': SECTORS.TENANT_ADMIN,  // SMART/FHIR integration page (platform_admin + tenant_admin)
  'invites':         SECTORS.TENANT_ADMIN,
  // Analytics (ops_manager, tenant_admin, specialist, platform_admin)
  analytics:         SECTORS.ANALYTICS,
  'business-metrics':SECTORS.ANALYTICS,
  'ops-home':        SECTORS.ANALYTICS,   // Stage 7 — all analytics-sector roles
  'performance':     SECTORS.ANALYTICS,   // Stage 7 — ops_manager + tenant_admin
  'deep-dive':       SECTORS.ANALYTICS,   // Stage 7 — ops_manager + tenant_admin
  'flow-quality':    SECTORS.ANALYTICS,   // Flow Quality Funnel — tenant_admin + ops_manager + platform_admin
  'ops-dashboard':   SECTORS.ANALYTICS,   // Ops Manager single-page dashboard

  // Audit
  'audit-logs':      SECTORS.AUDIT,

  // Ops
  ops:               SECTORS.OPS,

  // Workqueue
  workqueue:         SECTORS.WORKQUEUE,

  // Triage
  triage:                  SECTORS.TRIAGE,
  'triage-queue':          SECTORS.TRIAGE,
  'my-triage-history':     SECTORS.TRIAGE,
  'triage-queue-health':   SECTORS.TRIAGE,

  // Scheduling
  scheduling:             SECTORS.SCHEDULING,
  'scheduler-home':       SECTORS.SCHEDULING,

  // Specialist worklist
  'specialist-worklist':             SECTORS.SPECIALIST_WORKLIST,
  'specialist-admin-workplace':      SECTORS.SPECIALIST_WORKLIST,
  'specialist-admin-queue-health':   SECTORS.SPECIALIST_WORKLIST,
  'specialist-queue-health':         SECTORS.SPECIALIST_WORKLIST,

  // Closed-loop
  'closed-loop':          SECTORS.CLOSED_LOOP,

  // Pack Manager
  packs:             SECTORS.PACKS,

  // Gate
  gate:              SECTORS.GATE,

  // Packet Inbox (Phase 1)
  'packet-inbox':    SECTORS.PACKET_INBOX,

  // Not-yet / own submissions (gp_employee)
  'not-yet-inbox':   SECTORS.NOT_YET,
  'my-submissions':  SECTORS.NOT_YET,

  // Pending (no-op — handled before RouteGuard runs)
  pending:           SECTORS.PENDING,

  // 'dashboard' is role-specific — handled separately in renderView()
};

/** Returns all allowed routes for a role derived from sector allowlist. */
export const getAllowedRoutes = (role) => {
  const routes = getAllowedRoutesBase(role);
  // Settings is a universal personal/preferences page for any signed-in,
  // non-pending role (it edits only the user's own safe fields — never role,
  // tenant, or membership). Appended centrally so every role can reach it.
  if (role && role !== ROLES.PENDING && routes.length > 0) {
    return [...routes, 'settings'];
  }
  return routes;
};

const getAllowedRoutesBase = (role) => {
  switch (role) {
    case ROLES.PLATFORM_ADMIN:
      return [
        'dashboard', 'tenants', 'impact-settings', 'ehr-integration',
        'users', 'packs', 'phi-policy', 'ops', 'analytics',
        'workqueue', 'audit-logs', 'scheduling', 'closed-loop',
        'triage', 'not-yet-inbox', 'my-submissions', 'gate',
        'invites', 'flow-quality',
        'ops-home', 'performance', 'deep-dive', 'ops-dashboard',
        'attachment-viewer', 'packet-inbox',
      ];
    case ROLES.TENANT_ADMIN:
      return ['dashboard', 'business-metrics', 'audit-logs', 'users', 'invites', 'workqueue', 'ops',
              'ops-home', 'performance', 'deep-dive', 'ops-dashboard', 'impact-settings', 'ehr-integration',
              'referral-pings', 'attachment-viewer', 'packet-inbox']; // Stage 7
    case ROLES.OPS_MANAGER:
      return ['ops-dashboard', 'impact-settings', 'referral-pings', 'attachment-viewer', 'packet-inbox'];
    case ROLES.GP_EMPLOYEE:
      return ['dashboard', 'gate', 'not-yet-inbox', 'my-submissions', 'referral-pings', 'attachment-viewer', 'packet-inbox'];
    case ROLES.INTAKE_COORDINATOR:
      return ['workqueue', 'not-yet-inbox', 'gate', 'ops-home', 'referral-pings', 'attachment-viewer', 'packet-inbox']; // Stage 7
    case ROLES.TRIAGE_NURSE:
      return ['triage-queue', 'triage-queue-health', 'referral-pings', 'attachment-viewer'];
    case ROLES.SCHEDULER:
      return ['scheduling', 'scheduler-home', 'workqueue', 'ops-home', 'referral-pings', 'attachment-viewer']; // Stage 7
    case ROLES.SPECIALIST:
      return ['specialist-worklist', 'specialist-queue-health', 'packs', 'referral-pings', 'attachment-viewer'];
    case ROLES.SPECIALIST_ADMIN:
      return ['specialist-admin-workplace', 'specialist-admin-queue-health', 'referral-pings', 'attachment-viewer'];
    case ROLES.PENDING:
      return ['pending'];
    default:
      return [];
  }
};

export const canAccessRoute = (role, route) =>
  getAllowedRoutes(role).includes(route);

// ============================================================================
// REFERRAL PINGS — role catalogs (single source of truth)
// ============================================================================

// Roles that may participate in Referral Pings (page access, be a receiver).
// ops_manager intentionally excluded (analytics/oversight, not a ping action role).
export const REFERRAL_PINGS_ENABLED_ROLES = [
  ROLES.GP_EMPLOYEE,
  ROLES.INTAKE_COORDINATOR,
  ROLES.TRIAGE_NURSE,
  ROLES.SCHEDULER,
  ROLES.SPECIALIST,        // receive/reply only (consult-note pings) — not a creator
  ROLES.SPECIALIST_ADMIN,
  ROLES.TENANT_ADMIN,
];

// Roles that may CREATE a ping. gp_employee remains reply-only. specialist_admin
// creates from their closed-loop referral detail (CLOSED_LOOP_AVAILABLE).
export const REFERRAL_PINGS_CREATOR_ROLES = [
  ROLES.INTAKE_COORDINATOR,
  ROLES.TRIAGE_NURSE,
  ROLES.SCHEDULER,
  ROLES.SPECIALIST_ADMIN,
  ROLES.TENANT_ADMIN,
];

export const isReferralPingsEnabledRole = (role) =>
  REFERRAL_PINGS_ENABLED_ROLES.includes(role);

export const canCreateReferralPing = (role) =>
  REFERRAL_PINGS_CREATOR_ROLES.includes(role);

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// ============================================================================
// ANALYTICS VIEW-LEVEL ACCESS — Stage 9
// Differentiates within SECTOR_ANALYTICS (ops-home vs performance vs deep-dive).
// RouteGuard handles sector-level gating; AnalyticsGuard uses these for
// view-level gating.
// ============================================================================

/** View keys for analytics RBAC — passed to AnalyticsGuard as `requiredView`. */
export const ANALYTICS_VIEWS = {
  OPS:         'OPS',
  PERFORMANCE: 'PERFORMANCE',
  DEEP_DIVE:   'DEEP_DIVE',
};

const ANALYTICS_VIEW_ROLES = {
  [ANALYTICS_VIEWS.OPS]: [
    ROLES.PLATFORM_ADMIN, ROLES.TENANT_ADMIN,
    ROLES.INTAKE_COORDINATOR, ROLES.SCHEDULER,
    ROLES.SPECIALIST,
  ],
  [ANALYTICS_VIEWS.PERFORMANCE]: [
    ROLES.PLATFORM_ADMIN, ROLES.TENANT_ADMIN,
  ],
  [ANALYTICS_VIEWS.DEEP_DIVE]: [
    ROLES.PLATFORM_ADMIN, ROLES.TENANT_ADMIN,
  ],
};

/**
 * Returns true if the given role may access the specified analytics view.
 * @param {string} role  — user's role string (e.g. 'ops_manager')
 * @param {string} view  — one of ANALYTICS_VIEWS ('OPS' | 'PERFORMANCE' | 'DEEP_DIVE')
 */
export function canAccessAnalyticsView(role, view) {
  const allowed = ANALYTICS_VIEW_ROLES[view];
  if (!allowed) return false;
  return allowed.includes(role);
}

export default {
  ROLES,
  ROLE_ENUM,
  SECTORS,
  SECTOR_PERMISSIONS,
  DEFAULT_LANDING,
  defaultRouteForRole,
  ROUTE_TO_SECTOR,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  ROLE_DISPLAY,
  canAccessSector,
  isValidRole,
  hasPermission,
  canRunGate,
  canCreateMetrics,
  canManagePacks,
  canManageTenants,
  canManageAllUsers,
  canManageTenantUsers,
  canViewPlatformStats,
  canViewTenantAnalytics,
  canViewSpecialtyAnalytics,
  canViewOwnAnalytics,
  canExportAnalytics,
  isPlatformAdmin,
  isTenantAdmin,
  isOpsManager,
  isGpEmployee,
  isIntakeCoordinator,
  isTriageNurse,
  isScheduler,
  isSpecialist,
  isPending,
  isClinician,
  PACKET_UPLOAD_ROLES,
  canUploadPacketDocuments,
  getAllowedRoutes,
  canAccessRoute,
  ANALYTICS_VIEWS,
  canAccessAnalyticsView,
};
